import type { ReactNode } from 'react';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/Card';
import { MissingHints, MetricNotes } from './MissingHints';
import type { MissingRef } from '@/domain/types';

/**
 * 指标卡：标题 + 主数值 + 明细 + 缺失参数提示 + 口径说明。
 * 所有指标都长这样，用户扫一眼就知道「哪些算出来了、哪些还缺参数」。
 */
export function MetricCard({
  title,
  subtitle,
  value,
  unit,
  tone = 'neutral',
  badge,
  missing = [],
  notes = [],
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  value?: ReactNode;
  unit?: ReactNode;
  tone?: 'neutral' | 'ok' | 'info' | 'caution' | 'warning' | 'critical';
  badge?: ReactNode;
  missing?: readonly MissingRef[];
  notes?: readonly string[];
  children?: ReactNode;
}) {
  const toneClass =
    tone === 'neutral'
      ? 'text-fg'
      : tone === 'ok'
        ? 'text-ok'
        : tone === 'info'
          ? 'text-info'
          : tone === 'caution'
            ? 'text-caution'
            : tone === 'warning'
              ? 'text-warning'
              : 'text-critical';

  return (
    <Card className="print-keep">
      <CardHeader actions={badge}>
        <CardTitle subtitle={subtitle}>{title}</CardTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-2.5">
        {value !== undefined ? (
          <p className={['tabular text-xl leading-tight font-semibold', toneClass].join(' ')}>
            {value}
            {unit ? <span className="ml-1 text-xs font-normal text-fg-subtle">{unit}</span> : null}
          </p>
        ) : null}

        {children}

        {missing.length > 0 ? <MissingHints missing={missing} /> : null}
        {notes.length > 0 ? <MetricNotes notes={notes} /> : null}
      </CardBody>
    </Card>
  );
}
